import React, { useEffect, useState } from "react";
import { Card, Typography, Button, Tooltip, message, Tag, Skeleton, Grid } from "antd";
import { EnvironmentOutlined, PoweroffOutlined, ReloadOutlined } from "@ant-design/icons";
import { enableOrDisableGeofenceById, getGeofencesList } from "mqtt/location";
import { TbPolygon, TbCircle } from "react-icons/tb";

const { Text } = Typography;
const { useBreakpoint } = Grid;

const FenceRow = ({ fence, onPanTo, onToggle, toggling }) => {
  const isActive = fence.status === "active";
  const isPolygon = fence.type === "polygon";

  const typeCfg = isPolygon
    ? { Icon: TbPolygon, color: "#7c3aed", bg: "#f5f3ff", border: "#c4b5fd" }
    : { Icon: TbCircle, color: "#f97316", bg: "#fff7ed", border: "#fed7aa" };

  const detail = isPolygon
    ? `${fence.coordinates?.length || 0} coordinates`
    : `Radius: ${fence.radius?.toLocaleString() || "—"}m`;

  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: 10,
        border: `1px solid ${isActive ? "#bfdbfe" : "#e2e8f0"}`,
        background: isActive ? "#f8fbff" : "#fafafa",
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        transition: "border-color 0.2s, background 0.2s",
      }}
    >
      {/* type icon chip */}
      <div
        style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: typeCfg.bg, border: `1px solid ${typeCfg.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: typeCfg.color, fontSize: 20,
        }}
      >
        <typeCfg.Icon />
      </div>

      {/* name + metadata */}
      <div style={{ flex: 1, minWidth: 120 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>
          {fence.name || `Zone ${fence.id?.slice(0, 8) || "—"}`}
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {fence.description && (
            <span>{fence.description}</span>
          )}
          {fence.description && <span>·</span>}
          <span
            style={{
              fontFamily: "monospace", fontSize: 10,
              background: "#f1f5f9", padding: "0 5px", borderRadius: 3,
              border: "1px solid #e2e8f0",
            }}
          >
            {detail}
          </span>
          {fence.createdAt && (
            <>
              <span>·</span>
              <span>{new Date(fence.createdAt).toLocaleDateString()}</span>
            </>
          )}
        </div>
      </div>

      {/* type + status badges */}
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
        <Tag
          style={{
            margin: 0, borderRadius: 20, fontSize: 10,
            background: typeCfg.bg, border: `1px solid ${typeCfg.border}`, color: typeCfg.color,
          }}
        >
          {fence.type}
        </Tag>
        <Tag
          style={{
            margin: 0, borderRadius: 20, fontSize: 10,
            background: isActive ? "#f0fdf4" : "#f8fafc",
            border: `1px solid ${isActive ? "#86efac" : "#e2e8f0"}`,
            color: isActive ? "#16a34a" : "#94a3b8",
          }}
        >
          {isActive ? "● Active" : "○ Inactive"}
        </Tag>
      </div>

      {/* actions */}
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <Tooltip title="Pan to view on map">
          <Button
            size="small"
            icon={<EnvironmentOutlined />}
            onClick={() => onPanTo(fence)}
          />
        </Tooltip>
        <Button
          size="small"
          type={isActive ? "default" : "primary"}
          danger={isActive}
          loading={toggling}
          icon={<PoweroffOutlined />}
          onClick={() => onToggle(fence)}
          style={{ minWidth: 94 }}
        >
          {isActive ? "Deactivate" : "Activate"}
        </Button>
      </div>
    </div>
  );
};

const EmptyState = () => (
  <div
    style={{
      textAlign: "center", padding: "36px 16px",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
    }}
  >
    <div
      style={{
        width: 56, height: 56, borderRadius: 14,
        background: "#f1f5f9", border: "1px solid #e2e8f0",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 26, color: "#cbd5e1",
      }}
    >
      <EnvironmentOutlined />
    </div>
    <Text style={{ fontSize: 13, color: "#94a3b8" }}>No geofence zones configured yet</Text>
    <Text style={{ fontSize: 11, color: "#cbd5e1" }}>
      Use the Create Zone panel to draw your first geofence
    </Text>
  </div>
);

const GeoFencesList = ({ vehicle, handlePanToViewForGeofences, geofences, setGeofences }) => {
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(null); // fence id being toggled
  const { serialNo, vehicleKey } = vehicle || {};

  const fetchGeofences = async () => {
    setLoading(true);
    try {
      const response = await getGeofencesList(vehicleKey);
      setGeofences(response.data || []);
    } catch (err) {
      console.error("Error fetching geofences:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (serialNo) fetchGeofences();
    // eslint-disable-next-line
  }, [serialNo]);

  const handleToggle = async (fence) => {
    setToggling(fence.id);
    const newStatus = fence.status === "active" ? "inactive" : "active";
    const verb = newStatus === "active" ? "activated" : "deactivated";
    try {
      const response = await enableOrDisableGeofenceById({
        state: newStatus,
        geofenceId: fence.id,
      });
      if (response.status === 200) {
        await fetchGeofences();
        message.success(`"${fence.name}" ${verb} successfully`);
      }
    } catch {
      message.error("Failed to update geofence status");
    } finally {
      setToggling(null);
    }
  };

  const activeCount = geofences.filter((f) => f.status === "active").length;
  const inactiveCount = geofences.length - activeCount;

  return (
    <Card
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <EnvironmentOutlined style={{ color: "#2563eb" }} />
          <span>Geofence Zones</span>
        </div>
      }
      extra={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {geofences.length > 0 && (
            <>
              <Tag color="success" style={{ borderRadius: 20, fontSize: 10, margin: 0 }}>
                {activeCount} active
              </Tag>
              <Tag color="default" style={{ borderRadius: 20, fontSize: 10, margin: 0 }}>
                {inactiveCount} inactive
              </Tag>
            </>
          )}
          <Tooltip title="Refresh list">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={fetchGeofences}
            />
          </Tooltip>
        </div>
      }
      style={{ borderRadius: 12, border: "1px solid #e2e8f0" }}
    >
      {loading && geofences.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {[1, 2, 3].map((k) => (
            <Skeleton key={k} active paragraph={{ rows: 1 }} style={{ padding: "6px 0" }} />
          ))}
        </div>
      ) : geofences.length === 0 ? (
        <EmptyState />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {geofences.map((fence) => (
            <FenceRow
              key={fence.id}
              fence={fence}
              onPanTo={handlePanToViewForGeofences}
              onToggle={handleToggle}
              toggling={toggling === fence.id}
            />
          ))}
        </div>
      )}
    </Card>
  );
};

export default GeoFencesList;
